import { useCallback } from 'react';

import { useAddSeeds } from '@/hooks/useSeeds';
import type { SeedWizardFormData } from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';

/**
 * Props for useAddSeedSubmit hook.
 */
interface UseAddSeedSubmitProps {
  formData: SeedWizardFormData;
  onSuccess: () => void;
  onClose: () => void;
}

/**
 * Return type for useAddSeedSubmit hook.
 */
interface UseAddSeedSubmitReturn {
  handleSubmit: () => Promise<void>;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Hook for handling seed submission in the AddSeedByTypeModal.
 *
 * Simplified to only handle text input - file imports are now handled
 * via the ImportSeedsModal flow.
 */
export function useAddSeedSubmit({
  formData,
  onSuccess,
  onClose,
}: UseAddSeedSubmitProps): UseAddSeedSubmitReturn {
  const {
    mutateAsync: bulkAddSeeds,
    isPending,
    isError,
    error,
    reset,
  } = useAddSeeds();

  /**
   * Submit seeds to the API.
   * Parses text input into lines.
   */
  const handleSubmit = useCallback(async () => {
    if (!formData.seedType) return;

    // Parse text input: split by newlines, trim, filter empty
    const parsedSeeds = formData.textInput
      ? formData.textInput
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
      : undefined;

    if (!parsedSeeds || parsedSeeds.length === 0) {
      return;
    }

    try {
      await bulkAddSeeds({
        seeds: parsedSeeds,
        type: formData.seedType,
      });
      onSuccess();
      onClose();
    } catch {
      // Error handled by mutation state (isError, error)
      // Toast notifications handled by useAddSeeds internally
    }
  }, [formData, bulkAddSeeds, onSuccess, onClose]);

  return {
    handleSubmit,
    isPending,
    isError,
    error,
    reset,
  };
}
